import * as firebase from 'firebase/app';
import { every, filter, forEach, map } from 'lodash';

import { isNullOrUndef } from '../utils/misc';
import { db, MCValidationError } from '.';

/**
 * @param {Object[]} answers - Content of promptDefinition.answers
 * @returns {Object[]} The options that have `nextKey` === '.correct'
 * @private
 */
function _filterCorrectOptions(answers) {
  return filter(answers, answer => answer.nextKey === '.correct');
}

/**
 * @param {Object[]} correctAnswers - Content of promptDefinition.answers
 *   filtered to have only those options with `nextKey` === '.correct'
 * @returns {boolean} Whether the correct answers all have score 1000
 * @private
 */
function _checkCorrectAnswerScores(correctAnswers) {
  return every(correctAnswers, answer => answer.score === 1000);
}

/**
 * Checks whether answer scores are valid numbers and <= 1000. Adds a count of
 * the offending scores to the accumulator.
 * @param {Object[]} answers - Content of promptDefinition.answers
 * @param {{ nanCount: number, boundsCount: number }} acc - accumulator
 * @private
 */
function _checkAnswerScoresValidity(answers, acc) {
  if (typeof answers !== 'object') return;
  forEach(answers, answer => {
    if (!isNullOrUndef(answer.score) && isNaN(answer.score)) {
      acc.nanCount++;
    } else if (answer.score < 0 || answer.score > 1000) {
      acc.boundsCount++;
    }
  });
}

const requiredPromptsByType = {
  question: ['start', 'correct'],
  hint: ['start', 'hint', 'noHint']
};

function validateQuestion(docData, validationType) {
  let errors = [];
  const flowDefinition = docData.flowDefinition;
  const prompts = flowDefinition.prompts;
  requiredPromptsByType[validationType].forEach(promptKey => {
    if (!prompts.hasOwnProperty(promptKey)) {
      errors.push(`Missing prompt with key '${promptKey}'`);
    }
  });

  if (validationType === 'question') {
    let hasCorrectAnswers = false;
    let scoresAcc = { nanCount: 0, boundsCount: 0 };
    const commonAnswers = flowDefinition.answers;
    if (commonAnswers) {
      forEach(commonAnswers, answers => {
        _checkAnswerScoresValidity(answers, scoresAcc);
        const correctAnswers = _filterCorrectOptions(answers);
        if (correctAnswers.length > 0) {
          hasCorrectAnswers = true;
        }
        if (!_checkCorrectAnswerScores(correctAnswers)) {
          errors.push('Correct answer must have score 1000');
        }
      });
    }
    const promptAnswers = map(
      prompts,
      promptDefinition => promptDefinition.answers
    );
    forEach(promptAnswers, answers => {
      if (answers) {
        _checkAnswerScoresValidity(answers, scoresAcc);
        const correctAnswers = _filterCorrectOptions(answers);
        if (correctAnswers.length > 0) {
          hasCorrectAnswers = true;
        }
        if (!_checkCorrectAnswerScores(correctAnswers)) {
          errors.push('Correct answer must have score 1000');
        }
      }
    });
    if (!hasCorrectAnswers) {
      errors.push("Must have at least one answer with next key 'correct'");
    }
    if (scoresAcc.nanCount > 0) {
      errors.push(`${scoresAcc.nanCount} answers have non-number/blank scores`);
    }
    if (scoresAcc.boundsCount > 0) {
      errors.push(`${scoresAcc.boundsCount} answers have score < 0 or > 1000`);
    }
  }
  if (errors.length > 0) {
    throw new MCValidationError('Invalid question', errors);
  }
}

async function addQuestionInDB(ownerPath, addLocation) {
  const ownerId = db.doc(ownerPath);
  const questionData = {
    flowDefinition: {
      prompts: {},
      answers: {}
    },
    ownerId
  };

  const batch = db.batch();
  const newQuestionRef = db.collection('questions').doc();
  batch.set(newQuestionRef, questionData);
  batch.update(db.doc(addLocation), {
    questions: firebase.firestore.FieldValue.arrayUnion(newQuestionRef)
  });
  await batch.commit();
  return newQuestionRef;
}

async function getQuestionFromDB(id) {
  const ref = db.collection('questions').doc(id);
  const snapshot = await ref.get();
  return {
    id,
    ...snapshot.data()
  };
}

async function updateQuestionInDB(data, validationType) {
  const { id, ...docData } = data;
  validateQuestion(docData, validationType);
  await db
    .collection('questions')
    .doc(id)
    .update(docData);
}

export default {
  add: addQuestionInDB,
  get: getQuestionFromDB,
  update: updateQuestionInDB,
  validate: validateQuestion
};
